import { createInstance, dateReviver } from '@/api';
import {
    ProductTransformationCreateDto,
    ProductTransformationClient,
    ProductTransformationDetailDto,
    ProductTransformationUpdateDto,
    ProductEntityDetailDto,
    SourceEntityDetailDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useProductProcessDefinitionEditStore } from '../product-process-definition/product-process-definition-edit.store';
import { copyProductEntityValues } from '../../common';
import { copySourceEntityValues } from '../../common/source-entity.common';

export interface ProductTransformationStoreState {
    items?: { key: string; value: ProductTransformationDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new ProductTransformationClient('', createInstance(), dateReviver);

export const useProductTransformationStore = defineStore('configuration-product-transformation-store', {
    state: (): ProductTransformationStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        byDefinition: (state) => {
            return (definitionId: number): ProductTransformationDetailDto[] => {
                const key = `definition-${definitionId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        updateProductEntityUI(updatedProductEntity: ProductEntityDetailDto) {
            this.items?.forEach((item) => {
                item.value?.forEach((entity) => {
                    copyProductEntityValues(updatedProductEntity, entity.toEntity);
                });
            });
        },
        updateSourceEntityUI(updatedSourceEntity: SourceEntityDetailDto) {
            this.items?.forEach((item) => {
                item.value?.forEach((entity) => {
                    copySourceEntityValues(updatedSourceEntity, entity.fromEntity);
                });
            });
        },
        async getByDefinition(definitionId: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            const key = `definition-${definitionId}`;

            try {
                const response = await client.getByDefinition(definitionId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('producttransformation.detail.error.get.title'),
                    error,
                    false
                );
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(definitionId: number, dto: ProductTransformationCreateDto) {
            let hasError = false;
            this.reloading = true;
            try {
                const response = await client.create(dto);
                useAlertStore().setSuccess(i18n.global.t('producttransformation.detail.success.create.title'));
                this.getByDefinition(definitionId, true);
                return response.id;
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('producttransformation.detail.error.create.title'),
                    error
                );
            } finally {
                this.reloading = false;
                if(!hasError){
                    useProductProcessDefinitionEditStore().closeCreate();
                }
            }
        },
        async update(definitionId: number, transformationId: number, dto: ProductTransformationUpdateDto) {
            let hasError = false;
            this.reloading = true;
            try {
                await client.update(transformationId, dto);
                useAlertStore().setSuccess(i18n.global.t('producttransformation.detail.success.update.title'));
                this.getByDefinition(definitionId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(
                    i18n.global.t('producttransformation.detail.error.update.title'),
                    error
                );
            } finally {
                this.reloading = false;
                if(!hasError){
                    useProductProcessDefinitionEditStore().closeEdit();
                }
            }
        },
        async remove(definitionId: number, id: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('producttransformation.detail.success.delete.title'));
                await this.getByDefinition(definitionId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('producttransformation.detail.error.delete.title'),
                    error
                );
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(definitionId: number) {
            const key = `definition-${definitionId}`;

            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
